import React from 'react';
import { Container, Typography, Box, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContactInfo from './ContactInfo';
import ContactMap from './ContactMap';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = React.useState(() => {
    return i18n.language || localStorage.getItem('menuLanguage') || 'en';
  });

  // Sync language state with i18n.language when it changes (one-way: i18n -> state)
  React.useEffect(() => {
    if (i18n.language && i18n.language !== language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  // When user changes language via toggle, update localStorage and i18n
  const handleLanguageChange = React.useCallback((newLang) => {
    setLanguage(newLang);
    localStorage.setItem('menuLanguage', newLang);
    i18n.changeLanguage(newLang);
  }, [i18n]);

  return (
    <Box sx={{
      bgcolor: '#181818',
      minHeight: '100vh',
      pt: { xs: 8, md: 10 },
      pb: { xs: 8, md: 12 },
      px: { xs: 2, md: 4 },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '300px',
        pointerEvents: 'none'
      }
    }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <LanguageSwitcher language={language} setLanguage={handleLanguageChange} style="dark" floating={true} />

        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontFamily: 'Yeseva One, serif',
              mb: 2,
              background: 'linear-gradient(135deg, #fff 0%, #d19a06ff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', sm: '2.3rem', md: '2.5rem' },
              letterSpacing: { xs: 1, md: 2 },
              textShadow: '0 4px 20px rgba(237, 80, 52, 0.3)'
            }}
          >
            {t('contact.title')}
          </Typography>
          <Box sx={{
            width: { xs: 80, md: 120 },
            height: { xs: 5, md: 6 },
            background: 'linear-gradient(90deg, transparent, #db7603ff, transparent)',
            borderRadius: 3,
            mb: 3,
            mx: 'auto',
            boxShadow: '0 0 20px rgba(237, 80, 52, 0.5)'
          }} />
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 400,
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.25rem' },
              lineHeight: 1.6
            }}
          >
            {t('contact.subtitle', 'Посетете ни или се свържете с нас')}
          </Typography>
        </Box>

        {/* Map Section */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <ContactMap />
        </Box>

        {/* Quick Contact Buttons */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <ContactInfo />
        </Box>

        {/* Detailed Info Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(20, 20, 20, 0.9) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(237, 80, 52, 0.2)',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(237, 80, 52, 0.3)',
                  borderColor: 'rgba(237, 80, 52, 0.5)'
                }
              }}
            >
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ed5034 0%, #ff6b4a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 4px 20px rgba(237, 80, 52, 0.4)'
              }}>
                <LocationOnIcon sx={{ color: '#fff', fontSize: 32 }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1.5 }}>
                {t('contact.addressTitle', 'Адрес')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                {t('contact.fullAddress', '52 "Marin Drinov" str. 4000 Plovdiv, Bulgaria')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(20, 20, 20, 0.9) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(237, 80, 52, 0.2)',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(237, 80, 52, 0.3)',
                  borderColor: 'rgba(237, 80, 52, 0.5)'
                }
              }}
            >
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ed5034 0%, #ff6b4a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 4px 20px rgba(237, 80, 52, 0.4)'
              }}>
                <PhoneIcon sx={{ color: '#fff', fontSize: 32 }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1.5 }}>
                {t('contact.phoneTitle', 'Телефон')}
              </Typography>
              <Typography
                variant="body1"
                component="a"
                href="tel:+359878101363"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.6,
                  textDecoration: 'none',
                  display: 'block',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#ed5034' }
                }}
              >
                +359 878 101 363
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(20, 20, 20, 0.9) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(237, 80, 52, 0.2)',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(237, 80, 52, 0.3)',
                  borderColor: 'rgba(237, 80, 52, 0.5)'
                }
              }}
            >
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ed5034 0%, #ff6b4a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 4px 20px rgba(237, 80, 52, 0.4)'
              }}>
                <AccessTimeIcon sx={{ color: '#fff', fontSize: 32 }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1.5 }}>
                {t('contact.hoursTitle', 'Работно време')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                {t('contact.hours', 'Всеки ден: 09:00 - 00:00')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Contact;